import { useCallback, useEffect, useRef, useState } from 'react'
import { getPeso, iniciarLePesoBackground, type ResultadoPeso } from '@/lib/balanca'

export type StatusBalanca = 'AGUARDANDO' | 'DETECTADO' | 'RETIRAR'

const INTERVALO_POLL_MS = 250
const LEITURAS_PARA_REARMAR = 2
const MS_SEM_RESPOSTA_PARA_AVISO = 5000

export interface EstadoBalanca {
  status: StatusBalanca
  pesoKg: number | null
  mensagemErro: string | null
  confirmarAdicao: () => void
  cancelar: () => void
}

export function useBalancaTotem(ativo: boolean): EstadoBalanca {
  const [status, setStatusUI] = useState<StatusBalanca>('AGUARDANDO')
  const [pesoKg, setPesoKg] = useState<number | null>(null)
  const [mensagemErro, setMensagemErro] = useState<string | null>(null)
  const statusRef = useRef<StatusBalanca>('AGUARDANDO')
  const leiturasSemPesoRef = useRef(0)
  const semRespostaDesdeRef = useRef<number | null>(null)
  const definirStatus = useCallback((s: StatusBalanca) => {
    statusRef.current = s
    setStatusUI(s)
  }, [])

  const confirmarAdicao = useCallback(() => {
    setPesoKg(null)
    leiturasSemPesoRef.current = 0
    definirStatus('RETIRAR')
  }, [definirStatus])

  const cancelar = useCallback(() => {
    setPesoKg(null)
    leiturasSemPesoRef.current = 0
    definirStatus('RETIRAR')
  }, [definirStatus])

  const processar = useCallback(
    (r: ResultadoPeso) => {
      switch (statusRef.current) {
        case 'AGUARDANDO':
          if (r.status === 'PESO') {
            semRespostaDesdeRef.current = null
            setPesoKg(r.pesoKg)
            setMensagemErro(null)
            definirStatus('DETECTADO')
          } else if (r.status === 'ERRO' && r.code === 15) {
            if (semRespostaDesdeRef.current == null) semRespostaDesdeRef.current = Date.now()
            if (Date.now() - semRespostaDesdeRef.current >= MS_SEM_RESPOSTA_PARA_AVISO) {
              setMensagemErro(mensagemDeErro(r))
            }
          } else {
            semRespostaDesdeRef.current = null
            setMensagemErro(r.status === 'ERRO' ? mensagemDeErro(r) : null)
          }
          break
        case 'RETIRAR':
          if (r.status === 'PESO') {
            leiturasSemPesoRef.current = 0
          } else {
            leiturasSemPesoRef.current += 1
            if (r.status === 'VAZIO' || leiturasSemPesoRef.current >= LEITURAS_PARA_REARMAR) {
              leiturasSemPesoRef.current = 0
              definirStatus('AGUARDANDO')
            }
          }
          break
        case 'DETECTADO':
          break
      }
    },
    [definirStatus],
  )

  useEffect(() => {
    if (!ativo) {
      definirStatus('AGUARDANDO')
      setPesoKg(null)
      setMensagemErro(null)
      return
    }

    let cancelado = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const agendar = (): void => {
      timer = setTimeout(ciclo, INTERVALO_POLL_MS)
    }

    const ciclo = async (): Promise<void> => {
      if (cancelado) return
      if (statusRef.current === 'DETECTADO') {
        agendar()
        return
      }
      const resultado = await getPeso()
      if (cancelado) return
      processar(resultado)
      if (!cancelado) agendar()
    }

    void iniciarLePesoBackground()
    void ciclo()

    return () => {
      cancelado = true
      if (timer) clearTimeout(timer)
    }
  }, [ativo, processar, definirStatus])

  return { status, pesoKg, mensagemErro, confirmarAdicao, cancelar }
}

function mensagemDeErro(r: Extract<ResultadoPeso, { status: 'ERRO' }>): string | null {
  if (r.code === 10) return 'Balança indisponível. Rode o totem no app (Electron).'
  if (r.code === 15) return "Balança sem resposta. Verifique se o 'Sischef Le Peso' está aberto."
  if (r.code === -10) return 'Peso acima do limite da balança.'
  return null
}
